import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, Input, message, InputNumber } from 'antd';
import { documentService } from '../../services/documentService';

interface ApiResponse {
    req: {
        page: string;
        limit: string;
        search: string;
    };
    data: any[];
    total: number;
    currentPage: number;
    totalPages: number;
}

const DocumentManagement: React.FC = () => {
    const [documents, setDocuments] = useState<any[]>([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [currentDocument, setCurrentDocument] = useState<Partial<any>>({});
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalDocuments, setTotalDocuments] = useState(0);
    const [loading, setLoading] = useState(false);

    const fetchDocuments = useCallback(async () => {
        try {
            setLoading(true);
            const response: ApiResponse = await documentService.getDocuments({
                page: currentPage,
                pageSize: pageSize
            });
            setDocuments(response.data);
            setTotalDocuments(response.total);
        } catch (error) {
            message.error('Không thể tải danh sách tài liệu');
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize]);

    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 70
        },
        {
            title: 'Tên tài liệu',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Mô tả',
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: 'Giá',
            dataIndex: 'price',
            key: 'price',
            render: (price: number) => `${price} VND`
        },
        {
            title: 'Link ảnh',
            dataIndex: 'imageLink',
            key: 'imageLink',
        },
        {
            title: 'Trạng Thái',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <span className={`${status === '1' ? 'text-green-500' : 'text-red-500'}`}>
                    {status === '1' ? 'Hoạt Động' : 'Ngừng'}
                </span>
            ),
        },
        {
            title: 'Hành Động',
            key: 'actions',
            render: (text: string, record: any) => (
                <div className="flex space-x-2">
                    <Button size="small" onClick={() => handleEdit(record)}>Sửa</Button>
                    <Button size="small" danger onClick={() => handleDelete(record.id)}>Xóa</Button>
                </div>
            ),
        },
    ];

    const handleEdit = (document: any) => {
        setCurrentDocument(document);
        setIsModalVisible(true);
    };

    const handleDelete = async (documentId: string) => {
        try {
            await documentService.deleteDocument(documentId);
            message.success('Xóa tài liệu thành công');
            fetchDocuments();
        } catch (error) {
            message.error('Không thể xóa tài liệu');
        }
    };

    const handleSubmit = async () => {
        try {
            if (currentDocument.id) {
                await documentService.updateDocument(currentDocument);
                message.success('Sửa thông tin tài liệu thành công');
            } else {
                await documentService.createDocument(currentDocument as Omit<any, 'id'>);
                message.success('Lưu tài liệu mới thành công');
                setCurrentPage(1);
            }
            setIsModalVisible(false);
            fetchDocuments();
        } catch (error) {
            if (currentDocument.id) {
                message.error('Không thể sửa tài liệu');
            } else {
                message.error('Không thể lưu tài liệu');
            }
        }
    };

    const handleTableChange = (newPagination: any) => {
        setCurrentPage(newPagination.current);
        setPageSize(newPagination.pageSize);
    };

    return (
        <div className="p-6">
            <div className="flex justify-between mb-4">
                <h1 className="text-2xl font-bold">Quản Lý Tài Liệu</h1>
                <Button
                    type="primary"
                    onClick={() => {
                        setCurrentDocument({});
                        setIsModalVisible(true);
                    }}
                >
                    Thêm Tài Liệu
                </Button>
            </div>

            <Table
                tableLayout='fixed'
                columns={columns}
                dataSource={documents}
                rowKey="id"
                scroll={{ y: 600 }}
                pagination={{
                    size: 'small',
                    current: currentPage,
                    pageSize: pageSize,
                    total: totalDocuments,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    pageSizeOptions: [5, 10, 20],
                    showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
                }}
                loading={loading}
                onChange={handleTableChange}
            />

            <Modal
                title={currentDocument.id ? 'Sửa Tài Liệu' : 'Thêm Tài Liệu'}
                visible={isModalVisible}
                onOk={handleSubmit}
                onCancel={() => setIsModalVisible(false)}
            >
                <Form layout="vertical">
                    <Form.Item label="Tiêu đề tài liệu">
                        <Input
                            value={currentDocument?.name}
                            onChange={(e) => setCurrentDocument({ ...currentDocument, name: e.target.value })}
                        />
                    </Form.Item>
                    <Form.Item label="Mô tả">
                        <Input.TextArea
                            value={currentDocument?.description}
                            onChange={(e) => setCurrentDocument({ ...currentDocument, description: e.target.value })}
                            rows={5}
                        />
                    </Form.Item>
                    <Form.Item label="Giá">
                        <InputNumber
                            value={currentDocument?.price}
                            onChange={(e) => setCurrentDocument({ ...currentDocument, price: e })}
                            min={0}
                        />
                    </Form.Item>
                    <Form.Item label="Link ảnh">
                        <Input.TextArea
                            value={currentDocument?.imageLink}
                            onChange={(e) => setCurrentDocument({ ...currentDocument, imageLink: e.target.value })}
                            rows={3}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default DocumentManagement;